import { ref, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useChatStore } from '@/stores/useChatStore';
import { useSidebarStore } from '@/stores/useSidebarStore';
import { getCookie } from '@/utils/cookie';
import { useLocalStorage } from '@vueuse/core';

export function useResetChatOnAccountOrUserChange() {
  const store = useStore();
  const chatStore = useChatStore();
  const sidebarStore = useSidebarStore();

  // Store previous values for comparison
  const previousAccountId = ref(store.getters['auth/accountId']);
  const previousUserId = ref(store.getters['auth/user']?.id);

  // Store imitation token in localStorage to track changes across page reloads
  const storedImitationToken = useLocalStorage('stored_imitation_token', null);

  // Get current imitation token from cookie (only changes on app initialization)
  const currentImitationToken = ref(getCookie('imitation_token') || null);

  // Check if imitation state changed since last session and reset chat if needed
  if (storedImitationToken.value !== currentImitationToken.value) {
    sidebarStore.clearScoutOpenSession({
      userId: previousUserId.value,
      accountId: previousAccountId.value,
    });
    chatStore.resetState();
    storedImitationToken.value = currentImitationToken.value;
  }

  const stopWatcher = watch(
    () => {
      return {
        accountId: store.getters['auth/accountId'],
        userId: store.getters['auth/user']?.id
      };
    },
    (newValues) => {
      if (
        previousAccountId.value !== newValues.accountId ||
        previousUserId.value !== newValues.userId
      ) {
        sidebarStore.clearScoutOpenSession({
          userId: previousUserId.value,
          accountId: previousAccountId.value,
        });
        previousAccountId.value = newValues.accountId;
        previousUserId.value = newValues.userId;

        chatStore.resetState();
      }
    },
    { deep: true }
  );

  onUnmounted(() => {
    stopWatcher();
  });

  return {
    currentAccountId: previousAccountId,
    currentUserId: previousUserId,
    currentImitationToken: storedImitationToken,
    stopWatcher
  };
}
